import { useCallback, useEffect } from 'react';

import type { AccountDetailsState } from '../signupUtils';
import type { AuthConnection } from './useCreateAccount';

const deriveUserAuthConnection = (sub?: string): AuthConnection =>
  sub?.includes('google-oauth2')
    ? 'googleAuthConnection'
    : 'passwordAuthConnection';

type UseAuthConnectionsOptions = {
  user?: { sub?: string };
  inviteId?: string;
  authConnections: Record<AuthConnection, boolean>;
  setAccountDetails: (
    updater: (current: AccountDetailsState) => AccountDetailsState,
  ) => void;
};

export type ToggleAuthConnectionOptions = {
  connection: AuthConnection;
  checked: boolean;
};

export const useAuthConnections = ({
  user,
  inviteId,
  authConnections,
  setAccountDetails,
}: UseAuthConnectionsOptions) => {
  const userAuthConnection = deriveUserAuthConnection(user?.sub);

  const toggleAuthConnection = useCallback(
    ({ connection, checked }: ToggleAuthConnectionOptions) => {
      setAccountDetails(current => ({
        ...current,
        authConnections: {
          ...current.authConnections,
          [connection]: checked,
        },
      }));
    },
    [setAccountDetails],
  );

  useEffect(() => {
    if (!user) return;
    setAccountDetails(current => ({
      ...current,
      authConnections: !inviteId
        ? { googleAuthConnection: true, passwordAuthConnection: true }
        : {
            ...current.authConnections,
            [userAuthConnection]: true,
          },
    }));
  }, [user, inviteId, userAuthConnection, setAccountDetails]);

  const hasSelectedAuthConnection =
    Object.values(authConnections).some(Boolean);

  const showAuthConnections = !!inviteId;

  return {
    toggleAuthConnection,
    hasSelectedAuthConnection,
    showAuthConnections,
    userAuthConnection,
  };
};
